import { cantidadDe } from '@/lib/utils/formato-precio';
import type {
  EjemplosDelCambio,
  ResultadoDeSumarTienda,
  ResultadoDelCambioDeTienda,
} from '@/features/dynamic-pricing/services/piscina-service';
import { codigoDeError, mensajeDeError, type CodigoDeError } from '@/lib/api/mensaje-de-error';
import type { EstadoDeLaDemo, EstadoDeLaEmpresaEnPrueba } from './demo-service';

/**
 * Reglas de la demo (spec 343): el ÚNICO lugar del front que lee el estado de la demo y
 * el único con sus textos. Las pantallas reciben de acá qué mostrar: no comparan contadores
 * ni estados por su cuenta (lo vigila `estado-de-la-demo.guard.test.ts`).
 * La tienda de la prueba se nombra «la tienda que elegiste»: «tu tienda» se confunde con
 * la tienda propia del comercio.
 */
type EstadoDeLaTienda = EstadoDeLaEmpresaEnPrueba['tienda'];

export type SituacionDeLaTienda =
  | 'EN_USO'
  | 'PARA_ELEGIR'
  | 'PARA_CAMBIAR'
  | 'GUARDADA'
  | 'DEL_PLAN_COMPLETO';

interface Aviso {
  title: string;
  description?: string;
  variant?: 'success' | 'destructive';
}

export const TEXTOS_DE_LA_TIENDA = {
  etiquetaEnUso: 'En uso',
  sugerida: 'Sugerida',
  guardada: 'Guardada · Del plan completo',
  cambiar: 'Cambiar a esta',
  elegir: 'Elegir esta tienda',
  usar: 'Usar esta tienda',
  planCompleto: 'Plan completo',
  partePlanCompleto: 'Parte del plan completo',
  grupoElegida: 'La tienda que elegiste',
  grupoRubro: 'De tu rubro',
  grupoOtras: 'Otras tiendas',
  grupoGuardadas: 'Guardadas',
  soporte:
    'En una sesión de soporte no se cambia la tienda de la prueba: quedaría cambiada para la persona.',
  pedidoEnLaDemo: 'Cuando esté leída, aparece en esta lista.',
  noSePudo: 'No se pudo cambiar de tienda',
  pruebaDeNuevo: 'Prueba de nuevo en un momento.',
} as const;

/*
  Decisión 1 del 3-nov: la empresa nace sin tienda y la elige adentro. Mientras no la elija, las
  misiones y «Tiendas Prixia» lo dicen con estos textos (el estado lo decide el servidor).
*/
export const TEXTOS_DE_ELEGIR_TIENDA = {
  titulo: 'Elige la tienda de tu prueba',
  texto:
    'Es la tienda contra la que vas a comparar tus precios. Al elegirla se cargan tus productos de ejemplo y empiezan tus misiones. Puedes cambiarla después: la anterior queda guardada.',
  mision: 'Elige tu tienda',
  detalle: 'La tienda de tu rubro contra la que comparas tus precios.',
  boton: 'Elegir',
  siguiente: 'Primero: elige tu tienda →',
  cabecera: 'Primero elige tu tienda',
} as const;

/** ¿La empresa en prueba todavía no eligió su tienda? Lo dice el servidor (`tienda.estado`). */
export function faltaElegirTienda(estado: EstadoDeLaDemo | null | undefined): boolean {
  return !!estado && estado.esDemo === true && estado.tienda?.estado === 'NINGUNA';
}

/**
 * Decisión 3 del 3-nov: cómo se nombra la prueba en las misiones. Con socio, «Prueba con EGX One»;
 * sin socio (o sin saberlo todavía), «Tu prueba de Prixia».
 */
export function nombreDeLaPrueba(estado: EstadoDeLaDemo | null | undefined): string {
  const socio = estado && estado.esDemo ? estado.socio?.trim() : '';
  return socio ? `Prueba con ${socio}` : 'Tu prueba de Prixia';
}

export const AVISO_SIN_CONFIRMAR: Aviso = {
  title: 'Todavía no se pudo confirmar el cambio',
  description:
    'Puede seguir en curso. Conviene revisar la lista en un momento antes de volver a intentarlo.',
};

export function contadorDeTiendas(tienda: EstadoDeLaTienda): string {
  return `${tienda.usadas} de ${cantidadDe(tienda.tope, 'tienda', 'tiendas')}`;
}

export const esLaTiendaElegida = (tienda: EstadoDeLaTienda, sitio: string): boolean =>
  tienda.actual?.sitio === sitio;

/** Qué es cada tienda de la piscina para la demo. Lo decide el estado del servidor, no `enUso`. */
export function situacionDeLaTienda(
  fila: { sitio: string; archivadaEnLaDemo: boolean; elegible: boolean },
  tienda: EstadoDeLaTienda
): SituacionDeLaTienda {
  if (esLaTiendaElegida(tienda, fila.sitio)) return 'EN_USO';
  if (fila.archivadaEnLaDemo) return 'GUARDADA';
  if (!fila.elegible) return 'DEL_PLAN_COMPLETO';
  return tienda.estado === 'NINGUNA' ? 'PARA_ELEGIR' : 'PARA_CAMBIAR';
}

/** El texto del botón de cada situación; las que no tienen acción de cambio, `null`. */
export function textoDelBoton(situacion: SituacionDeLaTienda): string | null {
  if (situacion === 'PARA_ELEGIR') return TEXTOS_DE_LA_TIENDA.elegir;
  if (situacion === 'PARA_CAMBIAR') return TEXTOS_DE_LA_TIENDA.cambiar;
  if (situacion === 'GUARDADA') return TEXTOS_DE_LA_TIENDA.usar;
  return null;
}

export interface GrupoDeTiendas<T> {
  id: 'elegida' | 'rubro' | 'otras' | 'guardadas';
  titulo: string;
  tiendas: T[];
}

/** Los grupos de la lista de la demo, con el orden del servidor adentro y sin grupos vacíos. */
export function gruposDeLaPiscina<
  T extends { sitio: string; archivadaEnLaDemo: boolean; elegible: boolean; deTuRubro: boolean },
>(tiendas: T[], prueba: Pick<EstadoDeLaEmpresaEnPrueba, 'eje' | 'tienda'>): GrupoDeTiendas<T>[] {
  const { grupoElegida, grupoRubro, grupoOtras, grupoGuardadas } = TEXTOS_DE_LA_TIENDA;
  const grupos: GrupoDeTiendas<T>[] = [
    { id: 'elegida', titulo: grupoElegida, tiendas: [] },
    {
      id: 'rubro',
      titulo: prueba.eje.nombre ? `${grupoRubro} · ${prueba.eje.nombre}` : grupoRubro,
      tiendas: [],
    },
    { id: 'otras', titulo: grupoOtras, tiendas: [] },
    { id: 'guardadas', titulo: grupoGuardadas, tiendas: [] },
  ];
  const [elegida, rubro, otras, guardadas] = grupos;
  for (const t of tiendas) {
    const situacion = situacionDeLaTienda(t, prueba.tienda);
    if (situacion === 'EN_USO') elegida.tiendas.push(t);
    else if (situacion === 'GUARDADA') guardadas.tiendas.push(t);
    else if (t.deTuRubro) rubro.tiendas.push(t);
    else otras.tiendas.push(t);
  }
  return grupos.filter((g) => g.tiendas.length > 0);
}

const ejemplos = (n: number) => cantidadDe(n, 'producto de ejemplo', 'productos de ejemplo');

/** La confirmación antes de elegir, cambiar o volver. */
export function confirmacionDelCambio(
  situacion: SituacionDeLaTienda,
  nueva: string,
  actual: string | null
): { title: string; description: string; confirmText: string } {
  const queda = actual ?? TEXTOS_DE_LA_TIENDA.grupoElegida;
  if (situacion === 'PARA_ELEGIR') {
    return {
      title: `¿Elegir ${nueva}?`,
      description: `Es la tienda contra la que se compara en la prueba. Se cargan productos de ejemplo de ${nueva}. Después se puede cambiar: la anterior queda guardada.`,
      confirmText: TEXTOS_DE_LA_TIENDA.elegir,
    };
  }
  if (situacion === 'GUARDADA') {
    return {
      title: `¿Volver a ${nueva}?`,
      description: `${queda} y sus productos de ejemplo quedan guardados: no se borra nada. Vuelven los productos de ejemplo de ${nueva}, con los vínculos que tenía.`,
      confirmText: TEXTOS_DE_LA_TIENDA.usar,
    };
  }
  return {
    title: `¿Cambiar a ${nueva}?`,
    description: `${queda} y sus productos de ejemplo quedan guardados: no se borra nada y se puede volver. Se cargan productos de ejemplo de ${nueva}.`,
    confirmText: 'Cambiar de tienda',
  };
}

/* El catálogo ya no viaja en el pedido (cola `demo-entrega`): el cambio responde enseguida. */
export const esperaDelCambio = (nombre: string) => `Pasando a ${nombre}…`;

/*
  Fase 2 (carril Alta): el catálogo de la tienda llega en segundo plano. Mientras el servidor
  dice `catalogoListo: false`, Productos puede estar vacío unos segundos y lo avisa.
*/
export function catalogoEnCamino(estado: EstadoDeLaDemo | null | undefined): boolean {
  return !!estado && estado.esDemo === true && estado.catalogoListo === false;
}

export function textoDelCatalogoEnCamino(estado: EstadoDeLaDemo | null | undefined): string {
  const tienda = estado?.esDemo ? estado.tienda.actual?.nombre : undefined;
  return `Estamos trayendo el catálogo${tienda ? ` de ${tienda}` : ''}. Tus productos de ejemplo, el catálogo de la tienda y las sugerencias aparecen en unos segundos.`;
}

function textoDeEjemplos(nombre: string, e: EjemplosDelCambio): string | undefined {
  if (e.sembrados > 0 && e.desarchivados > 0) {
    return `Quedaron ${ejemplos(e.sembrados + e.desarchivados)} de ${nombre}.`;
  }
  if (e.sembrados > 0) {
    return `${e.sembrados === 1 ? 'Se cargó' : 'Se cargaron'} ${ejemplos(e.sembrados)} de ${nombre}.`;
  }
  if (e.desarchivados > 0) {
    return `${e.desarchivados === 1 ? 'Volvió' : 'Volvieron'} ${ejemplos(e.desarchivados)} de ${nombre}.`;
  }
  return undefined;
}

export function avisoDelCambio(r: ResultadoDelCambioDeTienda): Aviso {
  const { nombre } = r.nueva;
  if (!r.anterior) return { title: `${nombre} es la tienda que elegiste` };
  // La ruta no dice si la siembra quedó pendiente: 0 acá es que falló y se completa sola al ingresar.
  const deEjemplos =
    textoDeEjemplos(nombre, r.ejemplos) ??
    `Los productos de ejemplo de ${nombre} todavía no están listos: aparecen en el próximo ingreso.`;
  return {
    title: `Ahora se compara contra ${nombre}`,
    description: `${r.anterior.nombre} quedó guardada${r.ejemplos.archivados > 0 ? ' con sus ejemplos' : ''}. ${deEjemplos}`,
    variant: 'success',
  };
}

export function avisoDeLaEleccion(r: ResultadoDeSumarTienda): Aviso {
  const description = r.ejemplos ? textoDeEjemplos(r.nombre, r.ejemplos) : undefined;
  return {
    title: `Ahora se compara contra ${r.nombre}`,
    ...(description ? { description } : {}),
    variant: 'success',
  };
}

/*
  Tanda 3 (spec 343, filas Productos y Regla): el contador de productos propios y el freno. El
  servidor decide (el estado y el 403 con `codigo`); acá sólo se dice qué mostrar. El texto del
  freno es el del servidor, tal cual: el de acá es por si no manda ninguno.
*/
type EstadoDeLosProductos = EstadoDeLaEmpresaEnPrueba['productos'];

const productos = (n: number) => cantidadDe(n, 'producto', 'productos');

/** «37 de 100 productos»; sin tope, «37 productos». */
export function contadorDeProductos(p: EstadoDeLosProductos): string {
  return p.tope === null ? productos(p.usados) : `${p.usados} de ${productos(p.tope)}`;
}

/** ¿La prueba llegó al tope de productos propios? Lo dice el servidor, no la cuenta. */
export const productosEnElTope = (p: EstadoDeLosProductos): boolean => p.estado === 'EN_EL_TOPE';

export const CODIGOS_DEL_FRENO = [
  'TOPE_DE_PRODUCTOS_DEMO',
  'SOLO_EJEMPLOS_EN_LA_DEMO',
] as const satisfies readonly CodigoDeError[];

export type CodigoDelFreno = (typeof CODIGOS_DEL_FRENO)[number];

export const esCodigoDelFreno = (codigo: unknown): codigo is CodigoDelFreno =>
  (CODIGOS_DEL_FRENO as readonly unknown[]).includes(codigo);

export const TEXTOS_DEL_FRENO = {
  verElPlan: 'Ver el plan completo',
  ayudaDelContador:
    'Productos propios de la prueba. Los productos de ejemplo no cuentan. Los dados de baja sí.',
  filasFueraDelTope:
    'Algunas filas no entraron porque la prueba llegó a su tope de productos propios. Con el plan completo se carga todo el catálogo.',
} as const;

const DEL_FRENO: Record<CodigoDelFreno, { titulo: string; porDefecto: string }> = {
  TOPE_DE_PRODUCTOS_DEMO: {
    titulo: 'La prueba llegó a su tope de productos',
    porDefecto:
      'La prueba tiene un tope de productos propios. Con el plan completo se carga todo el catálogo.',
  },
  SOLO_EJEMPLOS_EN_LA_DEMO: {
    titulo: 'En la prueba, sólo con productos de ejemplo',
    porDefecto:
      'En la prueba, las reglas y estrategias se aplican a los productos de ejemplo. Con el plan completo, a todo tu catálogo.',
  },
};

/*
  Tienda de la prueba: rechazos con `codigo` al cambiar, elegir o reiniciar. El texto es el del
  servidor, tal cual; el de acá, por si no manda ninguno.
*/
const CODIGOS_DE_LA_TIENDA = [
  'SOPORTE_SOLO_MIRA',
  'DEMO_SIN_TIENDA',
  'EMPRESA_ARCHIVADA',
] as const satisfies readonly CodigoDeError[];

type CodigoDeLaTienda = (typeof CODIGOS_DE_LA_TIENDA)[number];

const DE_LA_TIENDA: Record<CodigoDeLaTienda, { titulo: string; porDefecto: string }> = {
  SOPORTE_SOLO_MIRA: {
    titulo: 'Con soporte sólo se mira',
    porDefecto: TEXTOS_DE_LA_TIENDA.soporte,
  },
  DEMO_SIN_TIENDA: {
    titulo: 'Falta elegir una tienda',
    porDefecto:
      'Esta prueba todavía no tiene una tienda elegida. Hay que elegir una antes de reiniciarla.',
  },
  EMPRESA_ARCHIVADA: {
    titulo: 'La empresa está archivada',
    porDefecto: 'Esta empresa está archivada. Hay que restaurarla antes de cambiarle la tienda.',
  },
};

/** El aviso (toast) de un rechazo de la tienda de la prueba; `null` si el error es otro. */
export function avisoDelErrorDeTienda(err: unknown): Aviso | null {
  const codigo = codigoDeError(err);
  if (!codigo || !(CODIGOS_DE_LA_TIENDA as readonly string[]).includes(codigo)) return null;
  const { titulo, porDefecto } = DE_LA_TIENDA[codigo as CodigoDeLaTienda];
  return { title: titulo, description: mensajeDeError(err, porDefecto), variant: 'destructive' };
}

export const tituloDelFreno = (codigo: CodigoDelFreno): string => DEL_FRENO[codigo].titulo;

export const textoDelFrenoPorDefecto = (codigo: CodigoDelFreno): string =>
  DEL_FRENO[codigo].porDefecto;

/** El pedido se cortó, pero el estado dice que el cambio quedó hecho. */
export const avisoDelCorte = (nombre: string): Aviso => ({
  title: `Ahora se compara contra ${nombre}`,
  description: 'Los productos de ejemplo pueden tardar un momento en aparecer.',
  variant: 'success',
});
